import type { ActivityDto, ConversationRowDto, WorkspaceKind, WorkspaceOverviewDto } from '@dbrain/shared';
import { AvatarStack, EmptyState, Icons, Menu, MenuItem, Progress, Segmented, Skeleton, Button } from '@dbrain/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCapabilities } from '../experience.js';
import { Link } from 'react-router';
import { useActivity, useDashboard, useWorkspaceOverview } from '../api/hooks.js';
import { ErrorAlert } from '../components/ErrorAlert.js';
import { ProfileIcon, statusOf, StatusBadge, useTimes } from '../components/status.js';
import { NewWorkspaceDialog } from '../components/workspaces/NewWorkspace.js';
import { homeOf, useExperience, useMeta } from '../experience.js';

/**
 * The workspaces (docs/design/mockups/CalismaAlanlari.dc.html): a card for
 * each, with what is in it, who is in it and when it was last used; the
 * recent conversations as rows with their status; what waits for the user
 * and what is running now.
 */
type Filter = 'all' | WorkspaceKind;

/** Where a workspace opens: its own experience's address (decision 2026-10-27). */
function useWorkspaceHref() {
  const meta = useMeta();
  return (kind: WorkspaceKind, id: string) => {
    const origin = homeOf(meta.data, kind);
    return origin ? `${origin}/w/${id}` : `/w/${id}`;
  };
}

/** "14 sohbet · 9 belge · 2 proje": what a workspace holds, the empty ones left out. */
export function useCountsLine() {
  const { t } = useTranslation();
  return (counts: WorkspaceOverviewDto['counts']) => {
    const parts = [t('workspaces.counts.conversations', { count: counts.conversations })];
    if (counts.documents > 0) parts.push(t('workspaces.counts.documents', { count: counts.documents }));
    if (counts.projects > 0) parts.push(t('workspaces.counts.projects', { count: counts.projects }));
    if (counts.skills > 0) parts.push(t('workspaces.counts.skills', { count: counts.skills }));
    return parts.slice(0, 3).join(' · ');
  };
}

function WorkspaceCard({ workspace, running }: { workspace: WorkspaceOverviewDto; running: boolean }) {
  const { t } = useTranslation();
  const times = useTimes();
  const href = useWorkspaceHref();
  const counts = useCountsLine();
  const people = workspace.members.people.map((p) => ({ id: p.userId, name: p.displayName }));
  const link = href(workspace.kind, workspace.id);
  const content = (
    <>
      <span className="workspace-card__head">
        <span className={`workspace-card__icon workspace-card__icon--${workspace.kind}`} aria-hidden="true">
          {workspace.kind === 'code' ? <Icons.code size={19} /> : <Icons.chat size={19} />}
        </span>
        <span className="workspace-card__titles">
          <span className="workspace-card__name">{workspace.name}</span>
          <span className={`workspace-card__kind workspace-card__kind--${workspace.kind}`}>{t(`kinds.${workspace.kind}`)}</span>
        </span>
      </span>
      <span className="workspace-card__counts">{counts(workspace.counts)}</span>
      <span className="workspace-card__foot">
        <AvatarStack people={people} label={t('workspaces.members', { count: workspace.members.count })} />
        {running ? (
          <span className="workspace-card__running">
            <span className="status-spinner" aria-hidden="true" />
            {t('status.running')}
          </span>
        ) : workspace.lastActivityAt ? (
          <span className="workspace-card__when">{times.since(workspace.lastActivityAt)}</span>
        ) : null}
      </span>
    </>
  );
  // a workspace of the other experience is a plain link to its own address
  return link.startsWith('/') ? (
    <Link className="workspace-card" to={link} data-testid="workspace-card" data-id={workspace.id}>
      {content}
    </Link>
  ) : (
    <a className="workspace-card" href={link} data-testid="workspace-card" data-id={workspace.id}>
      {content}
    </a>
  );
}

/** "Hukuk · nda-hazirla": where the conversation is and the most telling thing about it. */
export function useRowDetail() {
  const { t } = useTranslation();
  return (row: ConversationRowDto, withWorkspace = true) => {
    const { summary } = row;
    const parts = withWorkspace ? [row.workspaceName] : [];
    if (summary.progress && summary.progress.done < summary.progress.total) parts.push(t('workspaces.row.plan', { done: summary.progress.done + 1, total: summary.progress.total }));
    else if (summary.skills[0]) parts.push(summary.skills[0]);
    if (summary.files > 0) parts.push(t('workspaces.row.files', { count: summary.files }));
    else if (summary.documents > 0) parts.push(t('workspaces.row.documents', { count: summary.documents }));
    return parts.join(' · ');
  };
}

function RecentRows({ rows, activity }: { rows: ConversationRowDto[]; activity: ActivityDto | undefined }) {
  const { t } = useTranslation();
  const times = useTimes();
  const detail = useRowDetail();
  return (
    <section className="list-card workspaces-recent" aria-labelledby="workspaces-recent-title" data-testid="recent-conversations">
      <div className="list-card__head">
        <h2 id="workspaces-recent-title">{t('dashboard.recent.title')}</h2>
        <Link to="/history" className="list-card__more">
          {t('workspaces.all')}
        </Link>
      </div>
      {rows.length === 0 ? <p className="list-card__empty">{t('dashboard.recent.empty')}</p> : null}
      <ul className="row-list">
        {rows.map((row) => (
          <li key={row.id}>
            <Link to={`/c/${row.id}`} className="row-link" data-testid="recent-row">
              <ProfileIcon profile={row.profile} size={17} />
              <span className="row-link__text">
                <span className="row-link__title">{row.title ?? t('conversation.untitled')}</span>
                <span className="row-link__detail">{detail(row)}</span>
              </span>
              <StatusBadge status={statusOf(activity, row.id)} />
              <span className="row-link__time">{times.list(row.lastActivityAt)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** What waits for the user ("Sizi bekleyenler") and what runs ("Çalışanlar"). */
export function NowBoxes({ activity }: { activity: ActivityDto | undefined }) {
  const { t, i18n } = useTranslation();
  const waiting = activity?.pendingApprovals ?? [];
  const running = (activity?.activeRuns ?? []).filter((run) => !waiting.some((a) => a.conversationId === run.conversationId));
  const until = (iso: string | null) => (iso ? t('workspaces.now.until', { time: new Date(iso).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' }) }) : null);
  return (
    <aside className="now-boxes" aria-label={t('workspaces.now.label')}>
      <section className="now-box now-box--waiting" aria-labelledby="now-waiting" data-testid="now-waiting">
        <h2 id="now-waiting">
          <Icons.warning size={17} />
          {t('workspaces.now.waiting')}
          {waiting.length > 0 ? <span className="now-box__count">{waiting.length}</span> : null}
        </h2>
        {waiting.length === 0 ? <p className="now-box__empty">{t('workspaces.now.nothingWaits')}</p> : null}
        {waiting.map((item) => (
          <Link key={item.approvalId} to={`/c/${item.conversationId}`} className="now-box__item">
            <span className="now-box__title">{item.conversationTitle ?? t('conversation.untitled')}</span>
            <span className="now-box__detail">
              {[item.kind === 'question' ? t('workspaces.now.questions', { count: Math.max(1, item.questions) }) : t('workspaces.now.approval', { title: item.title || t(`dashboard.activity.kinds.${item.kind}`) }), until(item.expiresAt)]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </Link>
        ))}
      </section>
      <section className="now-box" aria-labelledby="now-running" data-testid="now-running">
        <h2 id="now-running">
          {/* the mark turns only while something works */}
          <span className={running.length > 0 ? 'status-spinner' : 'status-spinner status-spinner--still'} aria-hidden="true" />
          {t('workspaces.now.running')}
        </h2>
        {running.length === 0 ? <p className="now-box__empty">{t('workspaces.now.nothingRuns')}</p> : null}
        {running.map((run) => (
          <Link key={run.runId} to={`/c/${run.conversationId}`} className="now-box__item">
            <span className="now-box__title">
              <span>{run.title ?? t('conversation.untitled')}</span>
              {run.progress ? <span className="now-box__step">{`${run.progress.done}/${run.progress.total}`}</span> : null}
            </span>
            {run.progress ? (
              <Progress value={run.progress.done} max={run.progress.total} label={t('workspaces.now.progress', { done: run.progress.done, total: run.progress.total })} tone="running" />
            ) : (
              <span className="now-box__detail">{run.workspaceName}</span>
            )}
          </Link>
        ))}
      </section>
    </aside>
  );
}

export function WorkspacesPage() {
  const { t } = useTranslation();
  const capabilities = useCapabilities();
  const overview = useWorkspaceOverview();
  const dashboard = useDashboard();
  const activity = useActivity();
  const experience = useExperience();
  const [filter, setFilter] = useState<Filter>('all');
  const [creating, setCreating] = useState<WorkspaceKind | null>(null);
  // this experience's workspaces first (decision 2026-10-27)
  const workspaces = [...(overview.data ?? [])]
    .filter((w) => filter === 'all' || w.kind === filter)
    .sort((a, b) => Number(b.kind === experience) - Number(a.kind === experience));
  // a run that waits for the user is not "running": the card says so only while it works
  const waiting = new Set((activity.data?.pendingApprovals ?? []).map((a) => a.conversationId));
  const busyWorkspaces = new Set((activity.data?.activeRuns ?? []).filter((run) => !waiting.has(run.conversationId)).map((run) => run.workspaceId));

  return (
    <div className="page page--wide workspaces-page">
      <header className="page-header workspaces-page__header">
        <div className="page-header__text">
          <h1 className="page-header__title">{t('shell.workspaces')}</h1>
          <p className="page-header__intro">{t('workspaces.intro')}</p>
        </div>
        <div className="page-header__actions">
          {/* Phase 16a: one kind where the edition has no Code: nothing to filter, nothing to choose */}
          {capabilities.codeExperience ? (
            <Segmented
              label={t('workspaces.filter')}
              value={filter}
              onChange={setFilter}
              options={[
                { id: 'all', label: t('workspaces.filters.all'), 'data-testid': 'workspaces-filter-all' },
                { id: 'chat', label: t('workspaces.filters.chat'), 'data-testid': 'workspaces-filter-chat' },
                { id: 'code', label: t('workspaces.filters.code'), 'data-testid': 'workspaces-filter-code' },
              ]}
            />
          ) : null}
          {!capabilities.codeExperience ? (
            <Button icon={<Icons.plus size={16} />} onClick={() => setCreating('chat')} data-testid="new-chat-space">
              {t('shell.newWorkspace')}
            </Button>
          ) : (
          <Menu
            align="right"
            triggerClassName="ui-button ui-button--primary"
            trigger={
              <>
                <Icons.plus size={16} />
                {t('shell.newWorkspace')}
                <Icons.chevronDown size={14} />
              </>
            }
            data-testid="new-workspace-menu"
          >
            <MenuItem icon={<Icons.chat />} hint={t('workspaces.new.hint.chat')} onClick={() => setCreating('chat')} data-testid="new-chat-space">
              {t('kinds.chat')}
            </MenuItem>
            <MenuItem icon={<Icons.code />} hint={t('workspaces.new.hint.code')} onClick={() => setCreating('code')} data-testid="new-code-workspace">
              {t('kinds.code')}
            </MenuItem>
          </Menu>
          )}
        </div>
      </header>
      {creating ? <NewWorkspaceDialog initialKind={creating} onClose={() => setCreating(null)} /> : null}
      <ErrorAlert error={overview.error ?? dashboard.error} />

      <section aria-label={t('shell.workspaces')} data-testid="workspaces-card">
        {overview.isPending ? <Skeleton block /> : null}
        {overview.data && workspaces.length === 0 && filter !== 'all' ? <EmptyState icon={<Icons.grid />} title={t('workspaces.noneOfKind')} /> : null}
        <ul className="workspace-grid">
          {workspaces.map((workspace) => (
            <li key={workspace.id}>
              <WorkspaceCard workspace={workspace} running={busyWorkspaces.has(workspace.id)} />
            </li>
          ))}
          <li>
            <button type="button" className="workspace-card workspace-card--new" onClick={() => setCreating(filter === 'code' ? 'code' : experience)} data-testid="workspace-card-new">
              <span className="workspace-card__plus" aria-hidden="true">
                <Icons.plus size={18} />
              </span>
              <span className="workspace-card__new-title">{t('shell.newWorkspace')}</span>
              <span className="workspace-card__new-hint">{t('workspaces.newHint')}</span>
            </button>
          </li>
        </ul>
      </section>

      <div className="workspaces-page__lower">
        {dashboard.isPending ? <Skeleton lines={5} /> : <RecentRows rows={dashboard.data?.recentConversations ?? []} activity={activity.data} />}
        <NowBoxes activity={activity.data} />
      </div>
    </div>
  );
}
